// src/components/Zunftrat/zrCreateWorkAssignment.jsx
import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api from "../axiosSetup";
import "bootstrap/dist/css/bootstrap.min.css";
import CustomNavbar from "../CustomNavbar";
import Select from "react-select";
import 'bootstrap-icons/font/bootstrap-icons.css';
import cfg from '../../config';

const customStyles = {
  control: (styles) => ({
    ...styles,
    backgroundColor: "white",
    color: "black",
    borderColor: "#6c757d",
  }),
  singleValue: (styles) => ({
    ...styles,
    color: "black",
  }),
  menu: (styles) => ({
    ...styles,
    backgroundColor: "white",
  }),
  option: (styles, { isFocused, isSelected }) => ({
    ...styles,
    backgroundColor: isSelected ? "#007bff" : isFocused ? "#e9ecef" : "white",
    color: "black",
  }),
};

const ZRCreateWorkAssignment = () => {
  const [formData, setFormData] = useState({
    ID: "",
    name: "",
    einsatztermine: [{ datum: "", timeFrom: "", timeTo: "" }],
    personenanzahl: "",
    responsible: "",
    zugeordnetePersonen: [],
    zugeordneteZunftrat: [],
    bemerkung: "",
  });

  const [members, setMembers] = useState([]);
  const [zunftratMembers, setZunftratMembers] = useState([]);
  const [error, setError] = useState(null);
  const [termineWarnung, setTermineWarnung] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  // Mitglieder laden
  useEffect(() => {
    const fetchMembers = async () => {
      try {
        const token = localStorage.getItem("authToken");
        const response = await api.get("/api/member-workAssignment-zunftrat", {
          headers: { Authorization: `Bearer ${token}` },
        });
        setMembers(response.data || []);
      } catch (err) {
        console.error("Fehler beim Laden der Mitglieder:", err);
      }
    };
    fetchMembers();
  }, []);

  // Zunftrat-Mitglieder laden
  useEffect(() => {
    const fetchZunftratMembers = async () => {
      try {
        const token = localStorage.getItem("authToken");
        const response = await api.get("/api/memberZunftrat-workAssignment-zunftrat", {
          headers: { Authorization: `Bearer ${token}` },
        });
        setZunftratMembers(response.data || []);
      } catch (err) {
        console.error("Fehler beim Laden der Zunftrat-Mitglieder:", err);
      }
    };
    fetchZunftratMembers();
  }, []);

  // Edit-Modus initialisieren
  useEffect(() => {
    if (location.state) {
      const einsatz = location.state.einsatzItem;

      const einsatztermine =
        einsatz.datesData?.value?.map((item) => ({
          datum: item.workDate || "",
          timeFrom: item.timeFrom || "",
          timeTo: item.timeTo || "",
        })) || [{ datum: "", timeFrom: "", timeTo: "" }];

      setFormData((prev) => ({
        ...prev,
        ID: einsatz.ID || "",
        name: einsatz.Name?.value || "",
        einsatztermine,
        personenanzahl: Number(einsatz.numberOfPeople?.value ?? "") || "",
        responsible: einsatz.responsiblePerson?.value || "",
        zugeordnetePersonen: einsatz.attendance?.map((a) => a.memberID) || [],
        bemerkung: einsatz.bemerkung?.value || "",
        // nur Zunftrat-Mitglieder, die im aktuellen Pool existieren
        zugeordneteZunftrat:
          einsatz.zugeordneteZunftrat
            ?.map((a) => a.memberID)
            .filter((id) => (zunftratMembers || []).some((m) => m.ID === id)) || [],
      }));

      setIsEditMode(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state, zunftratMembers]);

  const memberOptions = (members || []).map((m) => {
    const haeserString =
      m.haeserNumbers?.length > 0 ? m.haeserNumbers.join(" | ") + " | " : "";
    return {
      value: m.ID,
      label: `${haeserString}${m.Vorname} ${m.Nachname}`,
    };
  });

  const zunftratOptions = (zunftratMembers || []).map((m) => ({
    value: m.ID,
    label: `${m.Vorname} ${m.Nachname}`,
  }));

  // Debug
  useEffect(() => {
    // console.log("Form Personen:", formData.zugeordnetePersonen);
    // console.log("Form Zunftrat:", formData.zugeordneteZunftrat);
  }, [formData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  /** ---------- Termin-Utils ---------- **/
  const addTermin = () => {
    setFormData((prev) => ({
      ...prev,
      einsatztermine: [...prev.einsatztermine, { datum: "", timeFrom: "", timeTo: "" }],
    }));
  };

  const removeTermin = (index) => {
    setFormData((prev) => {
      if (prev.einsatztermine.length === 1) return prev;
      const next = prev.einsatztermine.filter((_, i) => i !== index);
      return { ...prev, einsatztermine: next };
    });
  };

  const duplicateTermin = (index) => {
    setFormData((prev) => {
      const copy = { ...prev.einsatztermine[index] };
      const next = [...prev.einsatztermine];
      next.splice(index + 1, 0, copy);
      return { ...prev, einsatztermine: next };
    });
  };

  const updateTermin = (index, field, value) => {
    setFormData((prev) => {
      const next = [...prev.einsatztermine];
      next[index] = { ...next[index], [field]: value };
      return { ...prev, einsatztermine: next };
    });
  };

  const sortiereTermine = () => {
    setFormData((prev) => {
      const next = [...prev.einsatztermine].sort((a, b) => {
        const da = a.datum || "";
        const db = b.datum || "";
        if (da < db) return -1;
        if (da > db) return 1;
        const ta = (a.timeFrom || "00:00");
        const tb = (b.timeFrom || "00:00");
        return ta.localeCompare(tb);
      });
      return { ...prev, einsatztermine: next };
    });
  };

  const leereTermine = () => {
    setFormData((prev) => ({
      ...prev,
      einsatztermine: [{ datum: "", timeFrom: "", timeTo: "" }],
    }));
  };

  const hhmmToMinutes = (hhmm) => {
    const [h = "0", m = "0"] = (hhmm || "").split(":");
    return Number(h) * 60 + Number(m);
    };

  // Validierung: Pflicht, Von<Bis, keine Duplikate, keine Überlappungen pro Datum
  const validateTermine = () => {
    const rows = formData.einsatztermine || [];
    setTermineWarnung(null);

    // Ohne Termin faellt der Einsatz still aus der Tauschbörse: Sie prüft,
    // ob er in der Zukunft liegt, und ein fehlendes Datum ergibt dort nichts.
    if (!rows.length) {
      setError("Bitte mindestens einen Einsatztermin angeben.");
      return false;
    }

    // Ohne verantwortliche Person sieht niemand die Freigabeanfrage zu einem
    // Tausch — die Warteschlange filtert genau darauf.
    if (!(formData.zugeordneteZunftrat || []).length) {
      setError(`Bitte mindestens eine verantwortliche Person zuordnen (${cfg.adminRoleName}).`);
      return false;
    }

    // Pflichtfelder & Ordnung
    for (let i = 0; i < rows.length; i++) {
      const { datum, timeFrom, timeTo } = rows[i] || {};
      if (!datum || !timeFrom || !timeTo) {
        setError("Bitte fülle alle Felder bei den Einsatzterminen aus (Datum, Von, Bis).");
        return false;
      }
      if (timeFrom >= timeTo) {
        setError("Bei den Einsatzterminen muss die 'Von'-Zeit vor der 'Bis'-Zeit liegen.");
        return false;
      }
    }

    // Duplikate (exakt gleiche Kombination)
    const seen = new Set();
    for (const r of rows) {
      const key = `${r.datum}|${r.timeFrom}|${r.timeTo}`;
      if (seen.has(key)) {
        setError("Es gibt doppelte Einsatztermine (gleiches Datum und gleiche Zeitspanne).");
        return false;
      }
      seen.add(key);
    }

    // Überlappungen pro Datum
    const byDate = rows.reduce((acc, r) => {
      (acc[r.datum] ||= []).push(r);
      return acc;
    }, {});

    for (const [d, arr] of Object.entries(byDate)) {
      // sortiere am Datum nach from
      const sorted = [...arr].sort((a, b) => a.timeFrom.localeCompare(b.timeFrom));
      for (let i = 1; i < sorted.length; i++) {
        const prev = sorted[i - 1];
        const cur = sorted[i];
        const prevStart = hhmmToMinutes(prev.timeFrom);
        const prevEnd   = hhmmToMinutes(prev.timeTo);
        const curStart  = hhmmToMinutes(cur.timeFrom);
        // Überlappung, wenn Start < Ende des vorherigen
        if (curStart < prevEnd) {
          setError(`Zeitüberschneidung am ${d}: ${prev.timeFrom}-${prev.timeTo} und ${cur.timeFrom}-${cur.timeTo}.`);
          return false;
        }
      }
    }

    setError(null);
    return true;
  };

  /** ---------- Submit ---------- **/
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setTermineWarnung(null);

    try {
      // Validierung der Termine
      if (!validateTermine()) {
        setLoading(false);
        return;
      }

      // cleaned Termine + :00 anfügen
      const cleanedTermine = (formData.einsatztermine || [])
        .filter((t) => t.datum && t.timeFrom && t.timeTo)
        .map((t) => ({
          datum: t.datum,
          timeFrom: t.timeFrom.length === 5 ? `${t.timeFrom}:00` : t.timeFrom,
          timeTo: t.timeTo.length === 5 ? `${t.timeTo}:00` : t.timeTo,
        }));

      // Personenanzahl in Zahl casten
      const personenanzahlNum =
        formData.personenanzahl === "" ? null : Number(formData.personenanzahl);

      const payload = {
        ID: isEditMode ? Number(formData.ID) : undefined,
        name: formData.name,
        einsatztermine: cleanedTermine,
        personenanzahl: personenanzahlNum,
        responsible: formData.responsible,
        zugeordnetePersonen: (formData.zugeordnetePersonen || []).map(Number),
        zugeordneteZunftrat: (formData.zugeordneteZunftrat || []).map(Number),
        bemerkung: formData.bemerkung || "",
      };

      const token = localStorage.getItem("authToken");

      if (isEditMode) {
        // Serverseitig wird geprüft: URL :id muss = payload.ID sein
        await api.put(`/api/edit-workAssignment-zunftrat/${payload.ID}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        navigate("/zr-workassignment", {
          state: { message: "Der Arbeitseinsatz wurde erfolgreich bearbeitet!" },
        });
      } else {
        await api.post("/api/create-workAssignment-zunftrat", payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        navigate("/zr-workassignment", {
          state: { message: "Der Arbeitseinsatz wurde erfolgreich erstellt!" },
        });
      }
    } catch (err) {
      setError("Fehler beim Speichern: " + (err?.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <CustomNavbar />
      <div className="container mt-4">
        <h2 className="text-center mb-4">
          {isEditMode ? "Arbeitseinsatz bearbeiten" : "Arbeitseinsatz erstellen"}
        </h2>

        <form onSubmit={handleSubmit}>
          {/* Name */}
          <div className="mb-3">
            <label className="form-label">Name</label>
            <input
              type="text"
              className="form-control"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
            />
          </div>

          {/* Einsatztermine */}
          <div className="mb-3">
            <label className="form-label">Einsatztermine *</label>

            {formData.einsatztermine.map((termin, index) => (
              <div className="d-flex align-items-center gap-2 mb-2" key={index}>
                <input
                  type="date"
                  className="form-control"
                  style={{ maxWidth: 190 }}
                  value={termin.datum}
                  onChange={(e) => updateTermin(index, "datum", e.target.value)}
                  required
                />
                <span>Von:</span>
                <input
                  type="time"
                  className="form-control"
                  style={{ maxWidth: 150 }}
                  value={termin.timeFrom || ""}
                  onChange={(e) => updateTermin(index, "timeFrom", e.target.value)}
                  required
                />
                <span>Bis:</span>
                <input
                  type="time"
                  className="form-control"
                  style={{ maxWidth: 150 }}
                  value={termin.timeTo || ""}
                  onChange={(e) => updateTermin(index, "timeTo", e.target.value)}
                  required
                />

                {/* Aktionen pro Zeile */}
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  title="Termin duplizieren"
                  onClick={() => duplicateTermin(index)}
                >
                  <i className="bi bi-files"></i>
                </button>
                <button
                  type="button"
                  className="btn btn-outline-danger"
                  title="Termin entfernen"
                  onClick={() => removeTermin(index)}
                  disabled={formData.einsatztermine.length === 1}
                >
                  <i className="bi bi-trash"></i>
                </button>
              </div>
            ))}

            <div className="d-flex gap-2 mt-2">
              <button
                type="button"
                className="btn btn-outline-primary"
                onClick={addTermin}
              >
                + Termin hinzufügen
              </button>
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={sortiereTermine}
                title="Nach Datum und Startzeit sortieren"
              >
                Sortieren
              </button>
              <button
                type="button"
                className="btn btn-outline-warning"
                onClick={leereTermine}
                title="Alle Termine leeren"
              >
                Leeren
              </button>
            </div>

            {termineWarnung && (
              <div className="alert alert-warning mt-2">{termineWarnung}</div>
            )}
          </div>

          {/* Personenanzahl */}
          <div className="mb-3">
            <label className="form-label">benötigte Personenzahl</label>
            <input
              type="number"
              className="form-control"
              name="personenanzahl"
              value={formData.personenanzahl}
              onChange={handleChange}
              min={0}
              required
            />
          </div>

          {/* Verantwortlicher */}
          <div className="mb-3">
            <label className="form-label">Verantwortlicher inkl. Telefonnummer</label>
            <textarea
              className="form-control"
              name="responsible"
              value={formData.responsible}
              onChange={handleChange}
              rows={3}
              required
            />
          </div>

          {/* Personen zuordnen */}
          <div className="mb-3">
            <label className="form-label">Personen zuordnen</label>
            <Select
              isMulti
              options={memberOptions}
              value={memberOptions.filter((opt) =>
                (formData.zugeordnetePersonen || []).includes(opt.value)
              )}
              onChange={(selectedOptions) =>
                setFormData((prev) => ({
                  ...prev,
                  zugeordnetePersonen: (selectedOptions || []).map((opt) => opt.value),
                }))
              }
              styles={customStyles}
              placeholder="Personen auswählen..."
              isSearchable
            />
          </div>

          {/* Zunftrat Personen */}
          <div className="mb-3">
            <label className="form-label">Verantwortliche Personen zuordnen ({cfg.adminRoleName}) *</label>
            <Select
              isMulti
              options={zunftratOptions}
              value={zunftratOptions.filter((opt) =>
                (formData.zugeordneteZunftrat || []).includes(opt.value)
              )}
              onChange={(selectedOptions) =>
                setFormData((prev) => ({
                  ...prev,
                  zugeordneteZunftrat: (selectedOptions || []).map((opt) => opt.value),
                }))
              }
              styles={customStyles}
              placeholder={`${cfg.adminRoleName} auswählen...`}
              isSearchable
            />
          </div>

          {/* Bemerkung */}
          <div className="mb-3">
            <label className="form-label">Bemerkung</label>
            <textarea
              className="form-control"
              name="bemerkung"
              value={formData.bemerkung}
              onChange={handleChange}
              rows={3}
              placeholder="Hier kannst du eine Bemerkung hinzufügen..."
            />
          </div>

          {error && <div className="alert alert-danger">{error}</div>}

          <button type="submit" className="btn btn-primary" disabled={loading}>
            {isEditMode ? "Änderungen speichern" : "Erstellen"}
          </button>
        </form>
      </div>
      <br />
      <br />
    </div>
  );
};

export default ZRCreateWorkAssignment;